import type { EmergencyRole } from "../exported/shared/utils/emergencySession";
import { landingPath } from "./returnTo";

export interface AuthBroadcastDeps {
  hasActiveSession(): Promise<boolean>;
  // Offline, hasActiveSession() has to force a refresh it cannot complete, so
  // it answers false for a screen whose session is perfectly good. This is the
  // second opinion, and the only one available with cognito out of reach.
  hasOfflineSession(): Promise<boolean>;
  emergencyRole(): EmergencyRole | null;
  screenEmergencyRole(): EmergencyRole | null;
  isLoginRoute(): boolean;
  isAuthExempt(): boolean;
  consumeReturnTo(): string | null;
  navigate(url: string): void;
  reload(): void;
}

// Another screen's auth state changed — either it signed in (and this screen
// is stuck on /login) or it signed out (and this screen needs to follow it
// there). Trust the real session, not which direction the broadcast implies:
// a stale or out-of-order message must never contradict what Amplify has
// actually stored, which every screen shares via localStorage.
export async function handleAuthBroadcast(
  deps: AuthBroadcastDeps,
): Promise<void> {
  const emergencyRole = deps.emergencyRole();
  const emergency = emergencyRole !== null;
  const signedIn =
    emergency ||
    (await deps.hasActiveSession()) ||
    (await deps.hasOfflineSession());
  if (signedIn) {
    if (!deps.isLoginRoute()) {
      if (deps.screenEmergencyRole() !== emergencyRole) deps.reload();
      return;
    }
    deps.navigate(landingPath(emergency, deps.consumeReturnTo()));
  } else {
    if (deps.isAuthExempt()) return;
    deps.navigate("/login");
  }
}
